import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '../lib/cn'

// The status pill. Why the status tokens are backgrounds and not text colours, and why no
// status-to-variant map lives here: packages/ui/docs/nessuna-stringa-qui-dentro.md.

const badgeVariants = cva(
  'inline-flex w-fit shrink-0 items-center rounded-pill px-2.5 py-0.5 font-display text-note font-semibold tracking-wide whitespace-nowrap uppercase',
  {
    variants: {
      variant: {
        neutral: 'bg-muted text-muted-foreground',
        info: 'bg-info text-info-foreground',
        success: 'bg-success text-success-foreground',
        warning: 'bg-warning text-warning-foreground',
        danger: 'bg-danger text-danger-foreground',
        // Black on the page, white inside an ink island: `foreground` flips with the surface.
        ink: 'bg-foreground text-background',
        lime: 'bg-primary text-primary-foreground',
        special: 'bg-special text-special-foreground',
      },
      // `round` is the one-letter result (V / N / P): a circle, not a pill that happens to be short.
      shape: {
        pill: '',
        round: 'size-7 justify-center p-0',
      },
    },
    defaultVariants: {
      variant: 'neutral',
      shape: 'pill',
    },
  },
)

/** The emphasis scale, owned here. Exported so nothing re-declares it — it had three copies. */
export type StatusVariant = NonNullable<VariantProps<typeof badgeVariants>['variant']>

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {}

export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant, shape, ...props }, ref) => (
    // A `span` and not a `div`: a pill sits inside a cell, a heading or a line of text,
    // and a block element in there breaks the flow.
    <span ref={ref} className={cn(badgeVariants({ variant, shape }), className)} {...props} />
  ),
)
Badge.displayName = 'Badge'
